import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import websiteService from '../../../services/websiteService';
import { renderPlatformOptions } from '../../../data/platforms';

const AddWebsiteModal = ({ isOpen, onClose, onSave, isSubmitting, processingStatus = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    root_url: '',
    platform: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const [urlDebounceTimer, setUrlDebounceTimer] = useState(null);

  // Function to validate and check if URL already exists
  const isValidUrl = async (urlString, checkExisting = true) => {
    try {
      // Add protocol if missing
      let url = urlString.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      // Create URL object to validate
      const parsedUrl = new URL(url);
      
      // Allow both http and https protocols
      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
        return { isValid: false, message: 'URL must use either HTTP or HTTPS protocol' };
      }
      
      // Check if hostname is valid (contains at least one dot and no spaces)
      if (!parsedUrl.hostname.includes('.') || 
          parsedUrl.hostname.includes(' ') || 
          !parsedUrl.hostname.match(/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/)) {
        return { isValid: false, message: 'Please enter a valid domain name' };
      }
      
      // Check if TLD is reasonable length
      const tld = parsedUrl.hostname.split('.').pop();
      if (tld.length < 2) {
        return { isValid: false, message: 'Domain has an invalid TLD (top-level domain)' };
      }
      
      // Ensure URL is a root domain without paths
      if (parsedUrl.pathname !== '/' && parsedUrl.pathname !== '') {
        return { isValid: false, message: 'Please enter only the root domain without any pages or paths' };
      }
      
      // Ensure URL doesn't have query parameters
      if (parsedUrl.search !== '') {
        return { isValid: false, message: 'Please enter only the root domain without any query parameters' };
      }
      
      // Format the URL to be just the root domain with protocol
      const rootUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}`;
      
      // Check if this URL is already added by the user
      if (checkExisting) {
        try {
          setIsCheckingUrl(true);
          const response = await websiteService.getWebsiteByUrl(rootUrl);
          setIsCheckingUrl(false);
          
          // If we get a valid website back, it means it already exists
          if (response && response.website) {
            return { 
              isValid: false, 
              message: 'You already have a website with this URL. Please use a different URL.',
              formattedUrl: rootUrl 
            };
          }
        } catch (error) {
          setIsCheckingUrl(false);
          
          // We expect a 404 error if the website doesn't exist in our system
          // Just silently handle this as a successful validation case
          if (!error.response || error.response.status !== 404) {
            // Only log unexpected errors
            console.error('Error checking website URL existence:', error);
          }
          
          // Continue with validation regardless of error
          // 404 means the URL is available for use
        }
      }
      
      return { isValid: true, formattedUrl: rootUrl };
    } catch (e) {
      return { isValid: false, message: 'Please enter a valid URL' };
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Don't allow changes if submitting
    if (isSubmitting) return;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // If URL field is being edited, debounce check for existing URL
    if (name === 'root_url' && value.trim() !== '') {
      // Clear previous timer
      if (urlDebounceTimer) {
        clearTimeout(urlDebounceTimer);
      }
      
      // Set a new timer to check URL after user stops typing
      const timer = setTimeout(async () => {
        const urlValidation = await isValidUrl(value);
        if (urlValidation.isValid) {
          // Update the URL with properly formatted version
          setFormData(prev => ({
            ...prev,
            root_url: urlValidation.formattedUrl
          }));
        } else if (urlValidation.message.includes('already have a website')) {
          // Only show duplicate website error during typing
          setErrors(prev => ({
            ...prev,
            root_url: urlValidation.message
          }));
        }
      }, 800); // Wait 800ms after user stops typing
      
      setUrlDebounceTimer(timer);
    }
  };

  // Validate form before submission
  const validateForm = async () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Website name is required';
    }
    
    if (!formData.root_url.trim()) {
      newErrors.root_url = 'Website URL is required';
    } else {
      // Advanced URL validation with existence check
      const urlValidation = await isValidUrl(formData.root_url);
      if (!urlValidation.isValid) {
        newErrors.root_url = urlValidation.message;
      } else {
        // Update the URL with properly formatted version
        setFormData(prev => ({
          ...prev,
          root_url: urlValidation.formattedUrl
        }));
      }
    }
    
    // Check if platform is selected
    if (!formData.platform || !formData.platform.trim()) {
      newErrors.platform = 'Platform is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (await validateForm()) {
      onSave(formData);
    }
  };

  // Reset form when closed
  const handleClose = () => {
    // Don't allow closing if submitting
    if (isSubmitting) return;
    
    setFormData({
      name: '',
      root_url: '',
      platform: '',
      description: ''
    });
    setErrors({});
    
    // Clear any pending URL check
    if (urlDebounceTimer) {
      clearTimeout(urlDebounceTimer);
    }
    
    onClose();
  };

  // Clean up effect to clear timeout when component unmounts
  useEffect(() => {
    return () => {
      if (urlDebounceTimer) {
        clearTimeout(urlDebounceTimer);
      }
    };
  }, [urlDebounceTimer]);

  if (!isOpen) return null;

  // Define common disabled styles for form inputs when submitting
  const disabledStyle = isSubmitting ? 
    'bg-gray-100 cursor-not-allowed opacity-75' : 
    'bg-gray-50';

  // Get appropriate button text based on submission status
  const getSubmitButtonText = () => {
    if (!isSubmitting) return "Save Website";
    
    if (processingStatus) {
      return processingStatus;
    }
    
    return "Saving...";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto animate-fadeInUp">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-start justify-between">
          <h2 id="modal-title" className="text-xl font-bold text-gray-900">Add Website</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className={`text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full p-1 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Website Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`${disabledStyle} border ${errors.name ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                placeholder="My Website"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            
            {/* URL */}
            <div>
              <label htmlFor="root_url" className="block text-sm font-medium text-gray-700 mb-1">
                Website URL *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="root_url"
                  name="root_url"
                  value={formData.root_url}
                  onChange={handleChange}
                  disabled={isSubmitting || isCheckingUrl}
                  className={`${disabledStyle} border ${errors.root_url ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-8`}
                  placeholder="https://example.com"
                />
                {isCheckingUrl && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              {errors.root_url && <p className="mt-1 text-sm text-red-600">{errors.root_url}</p>}
              <p className="mt-1 text-xs text-gray-500">Enter only the root domain (e.g. example.com) without any paths or pages</p>
            </div>
            
            {/* Platform */}
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1">
                Platform *
              </label>
              <select
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`${disabledStyle} border ${errors.platform ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
              >
                {renderPlatformOptions()}
              </select>
              {errors.platform && <p className="mt-1 text-sm text-red-600">{errors.platform}</p>}
            </div>
            
            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={isSubmitting}
                rows={3}
                className={`${disabledStyle} border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                placeholder="Brief description of your website"
              />
            </div>

            <p className="text-sm text-gray-500">* Required fields</p>
          </div>
          
          {/* Footer */}
          <div className="p-5 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className={`px-4 py-2 bg-gray-200 text-gray-800 font-medium text-sm rounded-md hover:bg-gray-300 transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isCheckingUrl}
              className="px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {getSubmitButtonText()}
                </>
              ) : "Save Website"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddWebsiteModal;